/**
 * Geometria znaku Insolitum — JEDYNE źródło kształtu sygnetu w kodzie.
 *
 * Wzorcem są pliki z `docs/marka/insolitum/svg/` (pakiet z 05.10.2026).
 * Ten plik zapisuje ten sam znak LICZBAMI, bo z liczb da się zbudować
 * wszystko, czego plik SVG nie daje: animację każdej obręczy osobno, wersję
 * rozsypaną na stronę błędu, malarza we Flutterze i wektor w Androidzie.
 * Zgodność z wzorcem pilnuje `tests/unit/marka/geometriaZnaku.test.ts` —
 * porównuje ścieżki zbudowane stąd ze ścieżkami z plików pakietu, punkt po
 * punkcie, z tolerancją 0,001 jednostki.
 *
 * Układ współrzędnych: siatka 118 × 118, środek (59, 59), kąty w stopniach
 * EKRANOWYCH — 0° to „godzina trzecia", kąt rośnie ZGODNIE z ruchem
 * wskazówek zegara (oś y w dół, jak w SVG, Flutterze i Compose).
 *
 * Znak to tarcza i dwie obręcze o równej grubości (13,25) i równych
 * szczelinach (3,25). Każde cięcie jest RADIALNE i ma 30°.
 */

/** Wycinek pierścienia: promień wewnętrzny, zewnętrzny i zakres kątów. */
export interface WycinekPierscienia {
  /** Nazwa elementu — klucz animacji i identyfikator w generatorach. */
  readonly id: string;
  readonly r0: number;
  readonly r1: number;
  /** Kąt początkowy w stopniach ekranowych. */
  readonly od: number;
  /** Kąt końcowy (> `od`), w stopniach ekranowych. */
  readonly do: number;
}

export const SIATKA_SYGNETU = 118;
export const SRODEK_SYGNETU = 59;

/** Tarcza w środku sygnetu — „rdzeń" żywego systemu. */
export const PROMIEN_RDZENIA = 26;

/**
 * Obręcze sygnetu. Środkowa ma jedną szczelinę (−80°…−50°), zewnętrzna
 * dwie (0°…30° i 130°…160°). Kolejność jest kolejnością rysowania
 * w plikach wzorcowych.
 */
export const OBRECZE_SYGNETU: readonly WycinekPierscienia[] = [
  { id: 'srodkowa', r0: 29.25, r1: 42.5, od: -50, do: 280 },
  { id: 'zewnetrzna-gora', r0: 45.75, r1: 59, od: 160, do: 360 },
  { id: 'zewnetrzna-dol', r0: 45.75, r1: 59, od: 30, do: 130 },
];

/**
 * Sygnet uproszczony — do 32 px (favicon, zasobnik systemowy). Pełny znak
 * zlewa się w tak małym rozmiarze, więc zostaje tarcza i zewnętrzna obręcz.
 * Siatka 64 × 64, liczby z `docs/marka/insolitum/web/favicon.svg`.
 *
 * Promień wewnętrzny obręczy to 18,7443, nie 18,744: plik wzorcowy wypisuje
 * promień z trzema cyframi, ale wierzchołki liczył z dokładniejszej wartości
 * — z 18,744 jeden wierzchołek wychodzi 19,952 zamiast wzorcowego 19,951.
 * Przedział zgodny ze WSZYSTKIMI czterema wierzchołkami to [18,7442; 18,7445).
 */
export const SYGNET_UPROSZCZONY = {
  siatka: 64,
  srodek: 32,
  rdzen: 12.496,
  obrecze: [
    { id: 'zewnetrzna-gora', r0: 18.7443, r1: 30.72, od: 160, do: 360 },
    { id: 'zewnetrzna-dol', r0: 18.7443, r1: 30.72, od: 30, do: 130 },
  ] as readonly WycinekPierscienia[],
} as const;

/**
 * Pole ochronne: promień tarczy (≈ 22 % wysokości sygnetu). Wokół znaku
 * nic nie stoi bliżej niż tyle — dotyczy też animacji, które NIE wychodzą
 * poza siatkę powiększoną o to pole.
 */
export const POLE_OCHRONNE = PROMIEN_RDZENIA;

/**
 * Układy logo. Napis ma własną siatkę 302,76 × 43,66 i w obu układach
 * stoi w tej samej skali — różni się tylko przesunięciem (zmierzone na
 * plikach `insolitum-poziome-*.svg` i `insolitum-pionowe-*.svg`).
 */
export const NAPIS_SZEROKOSC = 302.76;
export const NAPIS_WYSOKOSC = 43.66;

export const UKLADY_LOGO = {
  poziome: {
    szerokosc: 439.05,
    wysokosc: 118,
    sygnet: { x: 0, y: 0 },
    napis: { x: 136.29, y: 37.17 },
  },
  pionowe: {
    szerokosc: 302.76,
    wysokosc: 173.46,
    sygnet: { x: 92.38, y: 0 },
    napis: { x: 0, y: 129.8 },
  },
} as const;

/**
 * Najmniejsza szerokość logo poziomego na ekranie — README pakietu
 * („logo poziome … 120 px na ekranie"). Wysokość z niej wynika: przy siatce
 * 439,05 × 118 to 32,25 px, więc „32 px", które wygląda na okrągłe minimum,
 * daje 119 px i łamie regułę o piksel. `LogoInsolitum` i `marka.css` nie
 * wpuszczają mniejszego.
 */
export const MIN_SZEROKOSC_POZIOMEGO_PX = 120;
export const MIN_WYSOKOSC_POZIOMEGO_PX =
  (MIN_SZEROKOSC_POZIOMEGO_PX * UKLADY_LOGO.poziome.wysokosc) / UKLADY_LOGO.poziome.szerokosc;

/**
 * Wysokość podpisu „część Insolitum" (logo poziome) — najmniejsza CAŁA
 * liczba pikseli, przy której logo ma ≥ 120 px szerokości (33 px). Pierwsze
 * wdrożenie wpisało 32 w piętnastu plikach, czyli 119 px.
 */
export const WYSOKOSC_PODPISU = Math.ceil(MIN_WYSOKOSC_POZIOMEGO_PX);

/** Znak pionowy (wstawka startowa, EkranStartowy, wejście na telefonie, `/m`). */
const PIONOWE_PX = 96;

/**
 * SKALA ZNAKU — jeden rozmiar na jedną rolę, w całym systemie (wysokość
 * układu w pikselach, o ile nie zaznaczono inaczej). Żaden ekran w `apps/`
 * nie wpisuje literału rozmiaru znaku; liczby z tej tabeli powtarza tylko
 * `marka.css` (bramka `tests/unit/marka/skalaZnaku.test.ts`).
 *
 * | klucz              | rola                                                      |
 * | ------------------ | --------------------------------------------------------- |
 * | `pionowe`          | znak pionowy: start, bramka „sprawdzam”, wejście na telefonie, `/m` |
 * | `naglowekWejscia`  | `LogoProduktu` w rogu ekranów wejścia na desktopie (48/48) |
 * | `naglowekTresci`   | logo poziome w nagłówku treści albo karty (oferta publiczna) |
 * | `chrom`            | goły sygnet w pasku bocznym i nagłówkach landingów         |
 * | `podpis`           | podpis „część Insolitum”, gdy w widoku nie ma sygnetu      |
 * | `napisPodpisu`     | podpis-napis, gdy sygnet już stoi (EkranBledu)             |
 * | `kafelOrganu`      | kafel organu jako postać ekranu (AuthGuard, bramka, B2B)   |
 * | `startWTresci`     | `EkranStartowy uklad="w-tresci"`                           |
 * | `ilustracja`       | sygnet ekranu stanu (desktop / < 640 px / kiosk w rem)     |
 * | `sygnetZPionowego` | sam sygnet o boku sygnetu ze znaku pionowego (PIN `/m`)    |
 * | `wezelPrzeplywu`   | żywy sygnet w węźle „Insolitum Universe” mapy przepływu     |
 * | `aktualizacja`     | żywy sygnet działu Aktualizacje okna agenta (jak Mobile 60) |
 *
 * Dwie liczby są wyprowadzone, nie wybrane:
 * - `naglowekWejscia` 45 → szerokość znaku 167,43 px ≈ szerokość napisu
 *   w pionowym 96 (167,55), więc hasło pod logo ma tę samą szerokość na
 *   telefonie i na desktopie;
 * - `napisPodpisu` = wysokość liter napisu w podpisie 33 (33 · 43,66 / 118
 *   = 12,21 px), czyli podpis-napis i podpis-logo mają te same litery.
 */
export const SKALA_ZNAKU = {
  pionowe: PIONOWE_PX,
  naglowekWejscia: 45,
  naglowekTresci: 36,
  chrom: 36,
  podpis: WYSOKOSC_PODPISU,
  napisPodpisu: (WYSOKOSC_PODPISU * NAPIS_WYSOKOSC) / SIATKA_SYGNETU,
  kafelOrganu: 64,
  startWTresci: 48,
  ilustracja: { desktop: 96, telefon: 80, kioskRem: 7.5 },
  sygnetZPionowego: (PIONOWE_PX * SIATKA_SYGNETU) / UKLADY_LOGO.pionowe.wysokosc,
  wezelPrzeplywu: 40,
  aktualizacja: 60,
} as const;

/** Kolory znaku z README pakietu. Akcent wyłącznie jako tło ikon. */
export const KOLORY_ZNAKU = {
  czern: '#0A0A0A',
  biel: '#FFFFFF',
  akcentIkony: '#3B82F6',
} as const;

// ──────────────────────────────────────────────────────────────────────
// Budowanie ścieżek SVG
// ──────────────────────────────────────────────────────────────────────

const f = (n: number): string => {
  const v = Math.round(n * 1000) / 1000;
  return (Object.is(v, -0) ? 0 : v).toFixed(3);
};

const punkt = (cx: number, cy: number, r: number, kat: number): string => {
  const rad = (kat * Math.PI) / 180;
  return `${f(cx + r * Math.cos(rad))},${f(cy + r * Math.sin(rad))}`;
};

/** Pełne koło jako ścieżka (dwa półłuki — tak jak w plikach wzorcowych). */
export function sciezkaKola(cx: number, cy: number, r: number): string {
  return (
    `M${f(cx + r)},${f(cy)}` +
    `A${f(r)},${f(r)} 0 1 0 ${f(cx - r)},${f(cy)}` +
    `A${f(r)},${f(r)} 0 1 0 ${f(cx + r)},${f(cy)}Z`
  );
}

/**
 * Wycinek pierścienia jako zamknięta ścieżka — w KOLEJNOŚCI plików
 * wzorcowych: łuk zewnętrzny od kąta końcowego wstecz do początkowego,
 * cięcie radialne, łuk wewnętrzny z powrotem. Ta kolejność daje ścieżkę
 * identyczną ze wzorcem co do znaku, więc test porównuje napisy, nie rastry.
 */
export function sciezkaWycinka(
  w: Pick<WycinekPierscienia, 'r0' | 'r1' | 'od' | 'do'>,
  cx: number = SRODEK_SYGNETU,
  cy: number = SRODEK_SYGNETU
): string {
  const duzy = w.do - w.od > 180 ? 1 : 0;
  return (
    `M${punkt(cx, cy, w.r1, w.do)}` +
    `A${f(w.r1)},${f(w.r1)} 0 ${duzy} 0 ${punkt(cx, cy, w.r1, w.od)}` +
    `L${punkt(cx, cy, w.r0, w.od)}` +
    `A${f(w.r0)},${f(w.r0)} 0 ${duzy} 1 ${punkt(cx, cy, w.r0, w.do)}Z`
  );
}

/** Cały sygnet jako jedna ścieżka (siatka 118). */
export function sciezkaSygnetu(): string {
  return (
    sciezkaKola(SRODEK_SYGNETU, SRODEK_SYGNETU, PROMIEN_RDZENIA) +
    OBRECZE_SYGNETU.map((w) => sciezkaWycinka(w)).join('')
  );
}

/** Sygnet uproszczony jako jedna ścieżka (siatka 64). */
export function sciezkaSygnetuUproszczonego(): string {
  const { srodek, rdzen, obrecze } = SYGNET_UPROSZCZONY;
  return (
    sciezkaKola(srodek, srodek, rdzen) +
    obrecze.map((w) => sciezkaWycinka(w, srodek, srodek)).join('')
  );
}

// ──────────────────────────────────────────────────────────────────────
// Organy — odłamki obręczy
// ──────────────────────────────────────────────────────────────────────

/**
 * SYMBOLIKA (właściciel, 05.10.2026): kula w środku to żywa sieć i Nexus,
 * odłamki na zewnątrz kuli to organy systemu. Cały ruch i każdy stan awarii
 * opowiada o relacji organów z siecią — dlatego rdzeń nigdy nie pęka:
 * organy mogą się rozsypać, zabłądzić albo stracić łączność, sieć zostaje.
 *
 * Odłamek to wycinek obręczy + przesunięcie wzdłuż dwusiecznej (jednostki
 * siatki) i obrót wokół własnego środka ciężkości.
 */
export type ObreczOdlamka = 'srodkowa' | 'zewnetrzna-gora' | 'zewnetrzna-dol';

export interface Odlamek extends WycinekPierscienia {
  /** Łuk wzorcowy, z którego odłamek pochodzi (scalanie nieruszonych sąsiadów). */
  readonly obrecz: ObreczOdlamka;
  /** Odsunięcie od środka znaku wzdłuż dwusiecznej wycinka. */
  readonly odsuniecie: number;
  /** Obrót odłamka wokół jego środka, w stopniach. */
  readonly obrot: number;
  /** Przesunięcie fazy dryfu (0–1) — odłamki nie falują unisono. */
  readonly faza: number;
}

/**
 * Podział na odłamki. Cięcia dodatkowe są RADIALNE, tak jak cięcia samego
 * znaku, i leżą wewnątrz łuków wzorcowych — każdy łuk jest pokryty swoimi
 * odłamkami bez szczeliny i bez zakładki (test), więc złożone z powrotem są
 * co do punktu znakiem wzorcowym.
 *
 * Odłamki są NIERÓWNE celowo: dwa drobne kawałki przy szczelinach i duże
 * łuki. Końce szczelin wzorcowych zostają na dużych kawałkach, więc nawet
 * rozsypana sylwetka pozostaje sygnetem, a nie tarczą strzelniczą. Lżejsze
 * odłamki odlatują dalej.
 *
 * Wartości są zaprojektowane, nie losowane: losowość przy każdym renderze
 * dawałaby migotanie przy hydratacji.
 */
export const ODLAMKI: readonly Odlamek[] = [
  // obręcz środkowa: −50° … 280°
  {
    id: 'srodkowa-1',
    obrecz: 'srodkowa',
    r0: 29.25,
    r1: 42.5,
    od: -50,
    do: 55,
    odsuniecie: 5,
    obrot: 4,
    faza: 0.1,
  },
  {
    id: 'srodkowa-2',
    obrecz: 'srodkowa',
    r0: 29.25,
    r1: 42.5,
    od: 55,
    do: 185,
    odsuniecie: 3,
    obrot: -3,
    faza: 0.6,
  },
  {
    id: 'srodkowa-3',
    obrecz: 'srodkowa',
    r0: 29.25,
    r1: 42.5,
    od: 185,
    do: 280,
    odsuniecie: 6,
    obrot: 6,
    faza: 0.35,
  },
  // obręcz zewnętrzna, łuk górny: 160° … 360°
  {
    id: 'zewnetrzna-gora-1',
    obrecz: 'zewnetrzna-gora',
    r0: 45.75,
    r1: 59,
    od: 160,
    do: 250,
    odsuniecie: 8,
    obrot: -5,
    faza: 0.8,
  },
  {
    id: 'zewnetrzna-gora-2',
    obrecz: 'zewnetrzna-gora',
    r0: 45.75,
    r1: 59,
    od: 250,
    do: 325,
    odsuniecie: 10,
    obrot: 7,
    faza: 0.2,
  },
  {
    id: 'zewnetrzna-gora-3',
    obrecz: 'zewnetrzna-gora',
    r0: 45.75,
    r1: 59,
    od: 325,
    do: 360,
    odsuniecie: 16,
    obrot: 14,
    faza: 0.95,
  },
  // obręcz zewnętrzna, łuk dolny: 30° … 130°
  {
    id: 'zewnetrzna-dol-1',
    obrecz: 'zewnetrzna-dol',
    r0: 45.75,
    r1: 59,
    od: 30,
    do: 95,
    odsuniecie: 9,
    obrot: -6,
    faza: 0.45,
  },
  {
    id: 'zewnetrzna-dol-2',
    obrecz: 'zewnetrzna-dol',
    r0: 45.75,
    r1: 59,
    od: 95,
    do: 130,
    odsuniecie: 15,
    obrot: -12,
    faza: 0.7,
  },
];

/**
 * Granica ruchu: żaden punkt odłamka — w żadnym stanie, łącznie ze
 * szczytem dryfu i pozycją startową składania — nie wychodzi dalej od
 * środka niż promień znaku plus pole ochronne (59 + 26). Pilnuje test.
 */
export const GRANICA_RUCHU = SRODEK_SYGNETU + POLE_OCHRONNE;

/**
 * Kolejność wejścia organów przy składaniu (mnożnik `--ids-dur-instant`):
 * najpierw obręcz środkowa, obręcz zewnętrzna rusza, zanim środkowa skończy.
 * Ostatni organ siada w `instant × 6 + slow` = 900 ms = `--ids-marka-skladanie`.
 * Jedno źródło dla CSS (`SygnetZywy`) i Compose (`ZnakInsolitumSciezki`).
 */
export const KOLEJNOSC_SKLADANIA: Readonly<Record<string, number>> = {
  'srodkowa-1': 1,
  'srodkowa-2': 2,
  'srodkowa-3': 3,
  'zewnetrzna-gora-1': 2,
  'zewnetrzna-gora-2': 3,
  'zewnetrzna-gora-3': 4,
  'zewnetrzna-dol-1': 5,
  'zewnetrzna-dol-2': 6,
};

/**
 * Pozycja startowa odłamka przy składaniu (organy zbierają się wokół
 * sieci): dalej niż przy rozpadzie, ale w granicy ruchu.
 */
export function startSkladania(o: Odlamek): { odsuniecie: number; obrot: number } {
  return {
    odsuniecie: Math.max(1.35 * o.odsuniecie, o.r0 < 40 ? 8 : 14),
    obrot: 1.35 * o.obrot,
  };
}

/** Środek ciężkości wycinka liczony po dwusiecznej (do obrotu odłamka). */
export function srodekWycinka(
  w: Pick<WycinekPierscienia, 'r0' | 'r1' | 'od' | 'do'>,
  cx: number = SRODEK_SYGNETU,
  cy: number = SRODEK_SYGNETU
): { x: number; y: number } {
  const kat = (((w.od + w.do) / 2) * Math.PI) / 180;
  const r = (w.r0 + w.r1) / 2;
  return { x: cx + r * Math.cos(kat), y: cy + r * Math.sin(kat) };
}

/** Wektor odsunięcia odłamka (dx, dy) wzdłuż jego dwusiecznej. */
export function wektorOdsuniecia(o: Pick<Odlamek, 'od' | 'do' | 'odsuniecie'>): {
  dx: number;
  dy: number;
} {
  const kat = (((o.od + o.do) / 2) * Math.PI) / 180;
  return { dx: o.odsuniecie * Math.cos(kat), dy: o.odsuniecie * Math.sin(kat) };
}
